import Backbone from 'backbone'
import _ from 'lodash'
import * as Canvas from 'canvas'
import $ from 'jquery'
import calendars from 'scoreboard/components/calendars'
import Dashboard from 'scoreboard/dashboards/dashboard'
import ViewOnlyCanvasContainerView from 'scoreboard/dashboards/display/view-only-canvas-container-view'
import { createWobs } from 'scoreboard/dashboards/display/wobs'
import { fetchDataOptions } from 'scoreboard/dashboards/edit/editable-canvas-container-view'
import Maestro from 'scoreboard/dashboards/widgets/maestro'

import template from './dashboard-widget.hbs'
import { MIN_HEIGHT, MIN_WIDTH } from './dashboard-widget-dimensions'
import Actor from '../actions/actor'
import buildDashboardWidgetFilter from './build-dashboard-widget-filter'

const DashboardWidget = Backbone.View.extend({

	tagName: 'li',

	className: 'dashboard-widget',

	/** @type {Form} */
	model: null,

	/**
	 * The view for dashboard widgets
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Form} options.model the form
	 *
	 * @param {FormWidget} options.widget the widget's data
	 *
	 * @param {FormDirector} [options.formDirector] when in View mode (not Edit),
	 * the FormDirector that is managing the form
	 */
	initialize: function(options = {}) {
		this.options = options
		this.dashboard = new Dashboard(options.widget.dashboard)
		if (options.widget.dashboardData) {
			this.dashboard.data.set(options.widget.dashboardData)
		}
		this.embeddedToken = options.formDirector?.options?.embeddedToken

		this.wobs = createWobs({
			dashboardModel: this.dashboard,
			organizationId: this.model.get('organizationId'),
			getPeriodId: () => this.getPeriodId()
		})

		// Can't use the normal fetchOptions for a view-only dashboard because
		// this might be a form widget that has been changed, but not yet saved.
		// So we need to pass along the widgets as currently configured, like
		// editable dashboards do
		this.dashboard.data.fetchOptions = this._fetchDataOptions.bind(this)

		if (!options.widget.dashboard.data) {
			// On the initial render for the View and Edit tabs, the data came
			// from the server. After a dashboard widget is edited, though,
			// the data needs to be refetched. We can detect this by the
			// absence of the data attribute.
			// Note that we have to wait until the canvas is rendered later,
			// though, to actually do the fetch
			this.initialFetchRequired = true
		}
		this.listenTo(this.options.formDirector, 'directed', this.handleRenderCallback)
	},

	render () {

		// If we're in Edit mode, we can immediately render the dashboard
		// (View mode needs to wait for Page Display Actions to complete)
		// #60292
		// If the widget has been made visible from a change-widget-state-actor,
		// we also need to render the dashboard immediately
		if (!this.options.allowInput ||
			this.options?.widget?.renderWidgetImmediately) {
			this.renderDashboard()
		}

		return this
	},

	renderDashboard: function() {
		// #58446
		// In instances such as changing navigation tabs, the forms-overview or
		// form-director-container may still not be visible when render is called.
		// As a result, we need to put in this safeguard and employ a listener
		// to ensure the dashboard widgets are rendered when both elements are
		// visible so that its canvas size is calculated correctly.
		const isFormViewVisible =
			!this.options.formView?.$el?.hasClass('is-not-displayed')
		const isFormDirectorContainerVisible =
			!$('.form-director-container').hasClass('is-not-displayed')
		if (isFormViewVisible && isFormDirectorContainerVisible) {
			// Once we've rendered the view, we can stop listening for directed.
			// This avoids bugs present for dash widgets with shape-views that
			// will cause a loop with their renderContent function.
			this.stopListening(this.options.formDirector, 'directed', this.handleRenderCallback)
			this.$el.empty().append(template())

			this.maestro = new Maestro({
				data: this.dashboard.data,
				embeddedToken: this.embeddedToken,
				state: this.buildDashboardState(),
				getPeriodId: () => this.getPeriodId()
			})

			this.renderCanvasContainer()

			if (this.initialFetchRequired) {
				this.dashboard.data.fetch({
					// Forms always use data from the default calendar period
					// (not globals.periodId)
					// https://github.com/SpiderStrategies/Scoreboard/issues/56207#issuecomment-1880112989
					calendarPeriodId: this.getPeriodId()
				})
			}
			this.canvasContainer.on('ready', () => {
				this.$el.trigger('ready')
			})
		}
	},

	getPeriodId () {
		// #56207 dashboards in a form always use the default period,
		// because there isn't a calendar period selector
		return calendars.defaultPeriodId()
	},

	handleRenderCallback: function() {
		// Use nextTick to ensure that the formDirector has finished
		// its set of actions before attempting a render
		process.nextTick(() => {
			this.render()
		})
	},

	_fetchDataOptions: function (opt = {}) {
		return fetchDataOptions(_.extend({
			wobs: this.wobs,
			model: this.dashboard,
			maestro: this.maestro
		}, opt))
	},

	renderCanvasContainer () {
		this.canvasContainer = new ViewOnlyCanvasContainerView({
			el: this.$('.canvas-container')[0],
			model: this.dashboard,
			wobs: this.wobs,
			maestro: this.maestro,
			embeddedToken: this.embeddedToken,
			canvasConstructor: Canvas.FullwidthCanvas,
			minHeight: MIN_HEIGHT,
			minWidth: MIN_WIDTH,
			// #58445 when displayed in a form, there should be no padding
			padding: 0
		})
		this.canvasContainer.render()
	},

	onDisplayActionsDone () {
		if (this.options.formDirector.widgetsView) {
			// Now that page display actions are done (applicable for View tab),
			// all inputs are set to their proper values, and we can render
			// the dashboard
			this.renderDashboard()

			// Wait until the form is totally rendered before listening for
			// change events
			this.listenForFilterSourceWidgetChange()
		}

		this.$el.trigger('displayActionsDone')
	},

	listenForFilterSourceWidgetChange () {
		this.listenTo(this.options.formDirector.widgetsView, 'change', ({ widgetId }) => {
			if (widgetId != this.options.widget.filterSourceWidgetId) {
				return
			}

			// The filter source widget has changed, so we need to update the
			// filter on the dashboard
			let updatedFilter = buildDashboardWidgetFilter({
				field: this.getFilterSourceWidgetField(),
				currentValue: this.getFilterSourceWidgetValue(),
				dataset: this.model.dataset
			})

			this.canvasContainer.maestro.changeSourceFilter({
				datasetId: this.model.dataset.id,
				filter: updatedFilter
			})
		})
	},

	getFilterSourceWidgetField () {
		let fieldId = this.model.widgetsById
			[this.options.widget.filterSourceWidgetId]?.fieldId
		return this.model.dataset.getField(fieldId)
	},

	buildDashboardState () {
		if (!this.options.formDirector) {
			// If there's no FormDirector, we're in Edit mode, and we don't
			// need to build dashboard state
			return null
		}

		let field = this.getFilterSourceWidgetField()

		if (_.isNil(field)) {
			// If no filter source has been defined, we don't have any state
			// to build
			return null
		}

		let datasetId = this.model.dataset.id

		let filter = buildDashboardWidgetFilter({
			field,
			currentValue: this.getFilterSourceWidgetValue(),
			dataset: this.model.dataset
		})

		if (!filter) {
			return null
		}

		return {
			liveFilters: {
				[datasetId]: [ filter ]
			}
		}
	},

	getFilterSourceWidgetValue () {
		let actor = new Actor({
			model: this.model,
			formView: this.options.formDirector.formView,
			formDirector: this.options.formDirector,
			record: this.options.formDirector.record
		})
		let record = actor.getData().record
		let field = this.getFilterSourceWidgetField()

		return field
			? record[actor.getRecordFieldIndex(field.id)]
			: null
	},

	// Note: autoRemove doesn't work because maestro is not a Backbone.View
	remove () {
		_.invokeMap([this.canvasContainer, this.maestro], 'remove')
	}

})

export default DashboardWidget

DashboardWidget.__filename__ = 'forms/widgets/dashboard-widget.js'
